import { useState, type ReactNode } from "react";
import { api, ApiError } from "../lib/api.js";
import type { ExportFormat } from "../lib/types.js";
import { Modal } from "../components/Modal.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

const FORMATS: { id: ExportFormat; label: string; desc: string; icon: IconName }[] = [
  { id: "pdf", label: "PDF", desc: "Print-ready book, typeset like a novel", icon: "file" },
  { id: "docx", label: "Word (.docx)", desc: "Editable manuscript for Word / Google Docs", icon: "file" },
  { id: "epub", label: "EPUB", desc: "E-reader format", icon: "book" },
  { id: "html", label: "HTML", desc: "Styled web page", icon: "file" },
  { id: "md", label: "Markdown", desc: "Plain-text with formatting", icon: "file" },
  { id: "txt", label: "Plain text", desc: "Just the words", icon: "file" }
];

/** PDF typography settings. Keys match the export route's query params. */
interface PdfOpts {
  trim: string;
  font: "serif" | "sans";
  size: number;
  spacing: number;
  margins: "narrow" | "normal" | "wide";
  sceneBreak: "asterisks" | "fleuron" | "space";
  pageNumbers: boolean;
  headers: boolean;
  chapterNewPage: boolean;
  chapterTitlePage: boolean;
  sceneTitlePage: boolean;
  mirror: boolean;
  titlePage: boolean;
}

// Professional defaults — a 6×9 trade paperback that needs no tweaking.
const DEFAULT_PDF: PdfOpts = {
  trim: "6x9",
  font: "serif",
  size: 11.5,
  spacing: 1.3,
  margins: "normal",
  sceneBreak: "asterisks",
  pageNumbers: true,
  headers: false,
  chapterNewPage: true,
  chapterTitlePage: true,
  sceneTitlePage: true,
  mirror: true,
  titlePage: true
};

const TRIMS: { id: string; label: string }[] = [
  { id: "6x9", label: '6" × 9" (US trade)' },
  { id: "5x8", label: '5" × 8"' },
  { id: "5.25x8", label: '5.25" × 8"' },
  { id: "5.5x8.5", label: '5.5" × 8.5"' },
  { id: "letter", label: 'Letter (8.5" × 11")' },
  { id: "a4", label: "A4" }
];

export function ExportModal({
  projectId,
  onClose,
  onImport
}: {
  projectId: string;
  onClose: () => void;
  onImport?: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [bookProjectBusy, setBookProjectBusy] = useState(false);
  const [view, setView] = useState<"formats" | "pdf">("formats");
  const [pdf, setPdf] = useState<PdfOpts>(DEFAULT_PDF);
  const setOpt = <K extends keyof PdfOpts>(key: K, value: PdfOpts[K]) => setPdf((p) => ({ ...p, [key]: value }));

  const exportAs = async (format: ExportFormat, opts?: Record<string, string | number | boolean>) => {
    setBusy(format);
    try {
      await api.downloadExport(projectId, format, opts);
      toast.success(`Exported as ${format.toUpperCase()}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Export failed");
    } finally {
      setBusy(null);
    }
  };

  if (view === "pdf") {
    return (
      <Modal
        title="Export PDF"
        subtitle="Book typography — sensible defaults are set for you"
        onClose={onClose}
        width={560}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setView("formats")} disabled={busy !== null}>
              <Icon name="chevronRight" size={14} style={{ transform: "rotate(180deg)" }} /> Back
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setPdf(DEFAULT_PDF)} disabled={busy !== null}>
              Reset to defaults
            </button>
            <button className="btn btn-primary" onClick={() => exportAs("pdf", { ...pdf })} disabled={busy !== null}>
              {busy === "pdf" ? "Exporting…" : "Download PDF"}
            </button>
          </>
        }
      >
        <div className="pdf-opts">
          <Field label="Trim size">
            <select className="select" value={pdf.trim} onChange={(e) => setOpt("trim", e.target.value)}>
              {TRIMS.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Body font">
            <select className="select" value={pdf.font} onChange={(e) => setOpt("font", e.target.value as PdfOpts["font"])}>
              <option value="serif">Serif (Times) — recommended</option>
              <option value="sans">Sans-serif (Helvetica)</option>
            </select>
          </Field>
          <Field label="Font size">
            <select className="select" value={pdf.size} onChange={(e) => setOpt("size", Number(e.target.value))}>
              {[10, 10.5, 11, 11.5, 12, 12.5, 13].map((s) => (
                <option key={s} value={s}>{s} pt</option>
              ))}
            </select>
          </Field>
          <Field label="Line spacing">
            <select className="select" value={pdf.spacing} onChange={(e) => setOpt("spacing", Number(e.target.value))}>
              <option value={1.15}>Compact</option>
              <option value={1.3}>Normal</option>
              <option value={1.5}>Relaxed</option>
            </select>
          </Field>
          <Field label="Margins">
            <select className="select" value={pdf.margins} onChange={(e) => setOpt("margins", e.target.value as PdfOpts["margins"])}>
              <option value="narrow">Narrow</option>
              <option value="normal">Normal</option>
              <option value="wide">Wide</option>
            </select>
          </Field>
          <Field label="Scene break">
            <select className="select" value={pdf.sceneBreak} onChange={(e) => setOpt("sceneBreak", e.target.value as PdfOpts["sceneBreak"])}>
              <option value="asterisks">* * *</option>
              <option value="fleuron">• • •</option>
              <option value="space">Blank space</option>
            </select>
          </Field>
        </div>

        <div className="pdf-toggles">
          <Toggle label="Title page" checked={pdf.titlePage} onChange={(v) => setOpt("titlePage", v)} />
          <Toggle label="Page numbers" checked={pdf.pageNumbers} onChange={(v) => setOpt("pageNumbers", v)} />
          <Toggle label="Chapters start on a new page" checked={pdf.chapterNewPage} onChange={(v) => setOpt("chapterNewPage", v)} />
          <Toggle label="Chapter title on its own page" checked={pdf.chapterTitlePage} onChange={(v) => setOpt("chapterTitlePage", v)} />
          <Toggle label="Scene title on its own page" checked={pdf.sceneTitlePage} onChange={(v) => setOpt("sceneTitlePage", v)} />
          <Toggle label="Mirror inside/outside margins" checked={pdf.mirror} onChange={(v) => setOpt("mirror", v)} />
          <Toggle label="Running headers (title / author)" checked={pdf.headers} onChange={(v) => setOpt("headers", v)} />
        </div>
      </Modal>
    );
  }

  const downloadBookProject = async () => {
    setBookProjectBusy(true);
    try {
      await api.downloadBookProject(projectId);
      toast.success("Book Project exported.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Export failed");
    } finally {
      setBookProjectBusy(false);
    }
  };

  return (
    <Modal title="Export manuscript" subtitle="Download your book in any format" onClose={onClose} width={560}>
      <div className="export-section-label">Reader formats</div>
      <div className="export-grid">
        {FORMATS.map((f) => (
          <button
            key={f.id}
            className="export-option"
            onClick={() => (f.id === "pdf" ? setView("pdf") : exportAs(f.id))}
            disabled={busy !== null}
          >
            <Icon name={busy === f.id ? "clock" : f.icon} size={20} className={busy === f.id ? "spin" : ""} style={{ color: "var(--accent)" }} />
            <div className="col" style={{ minWidth: 0 }}>
              <span className="strong">{f.label}</span>
              <span className="faint" style={{ fontSize: 11.5 }}>{f.desc}</span>
            </div>
            {f.id === "pdf" && <Icon name="chevronRight" size={16} style={{ color: "var(--text-faint)", marginLeft: "auto" }} />}
          </button>
        ))}
      </div>

      <div className="export-section-label" style={{ marginTop: 18 }}>Project / editing</div>
      <div className="export-grid">
        <button className="export-option" onClick={downloadBookProject} disabled={bookProjectBusy}>
          <Icon name={bookProjectBusy ? "clock" : "layers"} size={20} className={bookProjectBusy ? "spin" : ""} style={{ color: "var(--accent)" }} />
          <div className="col" style={{ minWidth: 0 }}>
            <span className="strong">Book Project (.zip)</span>
            <span className="faint" style={{ fontSize: 11.5 }}>Editable copy + reference data for external editing and round-trip import</span>
          </div>
        </button>
        {onImport && (
          <button className="export-option" onClick={onImport} disabled={bookProjectBusy}>
            <Icon name="undo" size={20} style={{ color: "var(--accent)" }} />
            <div className="col" style={{ minWidth: 0 }}>
              <span className="strong">Import Book Project…</span>
              <span className="faint" style={{ fontSize: 11.5 }}>Review and merge changes from an edited Book Project ZIP</span>
            </div>
            <Icon name="chevronRight" size={16} style={{ color: "var(--text-faint)", marginLeft: "auto" }} />
          </button>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="pdf-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
